import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import AdminLayout from '@/components/layouts/AdminLayout';
import { PageContainer } from '@/components/layouts/PageContainer';

// Module Header Skeleton
const TrainingModuleHeaderSkeleton: React.FC = () => (
  <div className="mb-12">
    <Skeleton className="h-3 w-20 mb-3" />
    <Skeleton className="h-9 w-80 max-w-full mb-3" />
    <Skeleton className="h-5 w-full max-w-2xl" />
  </div>
);

// Trait Card Skeleton (for Five Traits section)
const TraitExplainerSkeleton: React.FC = () => (
  <div className="border border-border/40 rounded-lg p-6">
    <div className="flex items-start gap-4">
      <Skeleton className="h-10 w-10 rounded-md flex-shrink-0" />
      <div className="flex-1 space-y-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-10/12" />
      </div>
    </div>
  </div>
);

// Chart Section Skeleton
const ChartSectionSkeleton: React.FC = () => (
  <div className="mb-20">
    <Skeleton className="h-8 w-64 mb-6" />
    <div className="border border-border/40 rounded-lg p-6 space-y-6">
      {/* Chart placeholder */}
      <div className="flex justify-center">
        <Skeleton className="h-32 w-1/2" />
      </div>
      {/* Legend */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="grid grid-cols-2 gap-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
      </div>
      {/* Description */}
      <div className="space-y-2 pt-4 border-t border-border/40">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  </div>
);

// Generic Section Skeleton
const SectionSkeleton: React.FC<{ hasChart?: boolean }> = ({ hasChart = false }) => (
  <div className="mb-20">
    <Skeleton className="h-8 w-56 mb-6" />
    <div className="border border-border/40 rounded-lg p-6 space-y-4">
      {hasChart && <Skeleton className="h-40 w-full mb-4" />}
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-10/12" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-9/12" />
    </div>
  </div>
);

// Role Cards Grid Skeleton
const RoleCardsSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="border border-border/40 rounded-lg p-6 space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        {/* Mini chart */}
        <div className="pt-4">
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    ))}
  </div>
);

// Training Content Skeleton (for /training with sidebar)
export const TrainingContentSkeleton: React.FC = () => {
  return (
    <AdminLayout>
      <PageContainer maxWidth="xl">
        {/* Page Header */}
        <div className="mb-12">
          <Skeleton className="h-10 w-96 mb-3" />
          <Skeleton className="h-6 w-full max-w-2xl" />
        </div>

        {/* Main Content with Sidebar */}
        <div className="flex gap-12">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-8 space-y-3">
              <Skeleton className="h-5 w-40 mb-6" />
              {[...Array(9)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 max-w-4xl">
            {/* MODULE 1: FOUNDATIONS */}
            <TrainingModuleHeaderSkeleton />
            
            {/* 1.1 Understanding the Chart */}
            <ChartSectionSkeleton />
            
            {/* 1.2 Percentile Scale */}
            <SectionSkeleton />

            {/* MODULE 2: THE FRAMEWORK */}
            <TrainingModuleHeaderSkeleton />
            
            {/* 2.1 Five Traits */}
            <div className="mb-20">
              <Skeleton className="h-8 w-48 mb-6" />
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <TraitExplainerSkeleton key={i} />
                ))}
              </div>
            </div>

            {/* 2.2 Profile Width */}
            <SectionSkeleton hasChart />
            
            {/* 2.3 Reasoning Styles */}
            <SectionSkeleton hasChart />
            
            {/* 2.4 Execution Speed Gap */}
            <SectionSkeleton hasChart />
            
            {/* 2.5 Cognitive Assessment */}
            <SectionSkeleton />

            {/* MODULE 3: APPLICATION */}
            <TrainingModuleHeaderSkeleton />
            
            {/* 3.1 Role Matching Examples */}
            <div className="mb-20">
              <Skeleton className="h-8 w-64 mb-6" />
              <RoleCardsSkeleton />
            </div>
            
            {/* 3.2 Talent Design */}
            <SectionSkeleton hasChart />

            {/* Responsible Use & Legal */}
            <div className="space-y-8 mb-12">
              <div className="border border-border/40 rounded-lg p-6 space-y-4">
                <Skeleton className="h-7 w-56" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-10/12" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </div>
              
              <div className="border border-border/40 rounded-lg p-6 space-y-4">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-10/12" />
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="text-center py-8">
              <Skeleton className="h-12 w-48 mx-auto rounded-lg" />
            </div>
          </div>
        </div>
      </PageContainer>
    </AdminLayout>
  );
};

// Training Public Skeleton (for /training-public without sidebar)
export const TrainingPublicSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - Mobile Optimized */}
      <div className="border-b bg-background px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto text-center space-y-3 sm:space-y-4">
          <Skeleton className="h-8 sm:h-10 w-72 sm:w-96 mx-auto" />
          <Skeleton className="h-5 sm:h-6 w-full max-w-2xl mx-auto" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Module 1 */}
        <TrainingModuleHeaderSkeleton />
        <ChartSectionSkeleton />
        <SectionSkeleton />

        {/* Module 2 */}
        <TrainingModuleHeaderSkeleton />
        
        {/* Five Traits */}
        <div className="mb-20">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <TraitExplainerSkeleton key={i} />
            ))}
          </div>
        </div>

        <SectionSkeleton hasChart />
        <SectionSkeleton hasChart />
        <SectionSkeleton hasChart />
        <SectionSkeleton />

        {/* Module 3 */}
        <TrainingModuleHeaderSkeleton />
        
        {/* Role Cards */}
        <div className="mb-20">
          <Skeleton className="h-8 w-64 mb-6" />
          <RoleCardsSkeleton />
        </div>
        
        <SectionSkeleton hasChart />

        {/* Legal Sections */}
        <div className="space-y-8 mb-12">
          <div className="border border-border/40 rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4">
            <Skeleton className="h-5 sm:h-6 w-40 sm:w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
          <div className="border border-border/40 rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4">
            <Skeleton className="h-5 sm:h-6 w-40 sm:w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        </div>
      </div>

      {/* Bottom CTA - Mobile Optimized */}
      <div className="border-t bg-muted/30 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-3 sm:space-y-4">
          <Skeleton className="h-7 sm:h-8 w-48 sm:w-64 mx-auto" />
          <Skeleton className="h-10 sm:h-12 w-40 sm:w-48 mx-auto rounded-lg" />
        </div>
      </div>
    </div>
  );
};
